import * as React from "react";
import {
  TextBox,
  TextBoxChangeEvent,
  TextBoxHandle,
  TextBoxProps,
} from "@progress/kendo-react-inputs";
import useEmailValidator from "shared/hooks/common/useEmailValidator";
import CPErrorMessage from "../CPErrorMessage/CPErrorMessage";
import { StackLayout } from "@progress/kendo-react-layout";
import { FloatingLabel } from "@progress/kendo-react-labels";
import { RegExpPatterns, filterValidCharacters } from "shared/helpers";

interface CPEmailTextBoxProps extends Omit<
  TextBoxProps,
  "value" | "onChange" | "onBlur" | "required"
> {
  /** Current email string value. */
  value?: string;
  /** When `true` (default), enables inline email format validation. */
  useValidator?: boolean;
  /** Floating label rendered above the input. */
  label?: string;
  /** Error message displayed in red below the input. */
  errorMsg?: string;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `name` to resolve a field-level error. */
  error?: any;
  /** Callback fired on every value change. Receives a normalised `CPEmailTextBoxChangeEvent`. */
  onChange?: (event: CPEmailTextBoxChangeEvent) => void;
  /** Callback fired when the input loses focus. Receives a normalised `CPEmailTextBoxEvent`. */
  onBlur?: (evemt: CPEmailTextBoxEvent) => void;
  /** Marks the field as required for validation purposes. */
  required?: boolean;
}

const defaultErrorMsg = "Please enter a valid email address.";

export interface CPEmailTextBoxChangeEvent extends Omit<
  CPEmailTextBoxEvent,
  "type"
> {
  type: "CPEmailTextBoxChangeEvent";
}

export interface CPEmailTextBoxEvent {
  name: string;
  type: "CPEmailTextBoxEvent";
  syntheticEvent: React.ChangeEvent<HTMLInputElement>;
  value: React.InputHTMLAttributes<HTMLInputElement>["value"];
  error: string | undefined;
  isValid: boolean;
  target: TextBoxHandle;
}

/**
 * Email address input with built-in format validation. Filters input to characters valid in an
 * email address and fires normalised events that include the field `name` and `isValid` flag.
 *
 * @example
 * ```tsx
 * <CPEmailTextBox
 *   name="email"
 *   label="Email Address"
 *   required
 *   value={form.email}
 *   onChange={handleChange}
 *   errorMsg={errors.email}
 * />
 * ```
 */
const CPEmailTextBox: React.FC<CPEmailTextBoxProps> = (props) => {
  const required = props.required ?? props.useValidator ?? true;
  const { isValid } = useEmailValidator(props.value ?? "", required);

  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (props.error != null) setDirty(false);
  }, [props.error]);

  const formatString = React.useCallback((value: string | undefined) => {
    if (value == null || value.length === 0) return "";

    return filterValidCharacters(value, RegExpPatterns.validEmailCharacters);
  }, []);

  const handleOnChange = (event: TextBoxChangeEvent) => {
    if (props.onChange) {
      setDirty(true);
      const value = formatString(event.value as string);

      props.onChange({
        name: props.name ?? "email",
        type: "CPEmailTextBoxChangeEvent",
        value: value,
        syntheticEvent: event.syntheticEvent,
        target: event.target,
        isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const handleBlur = (event: React.FocusEvent<any, Element>) => {
    if (props.onBlur) {
      props.onBlur({
        name: props.name ?? "email",
        type: "CPEmailTextBoxEvent",
        value: event.target.value ?? props.value,
        syntheticEvent: event,
        target: event.target,
        isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const renderTextBox = () => {
    return (
      <TextBox
        //{...props}
        fillMode={props.fillMode ?? "solid"}
        size={props.size ?? "medium"}
        autoComplete="off"
        name={props.name ?? "email"}
        placeholder={props.placeholder ?? "Email Address"}
        onChange={handleOnChange}
        onBlur={handleBlur}
        value={props.value ?? ""}
        type="email"
      />
    );
  };
  return (
    <StackLayout orientation="vertical">
      {props.label != null ? (
        <FloatingLabel
          label={props.required ? `${props.label} *` : props.label}
          editorValue={props.value as string}
          editorId={props.name ?? "email"}
        >
          {renderTextBox()}
        </FloatingLabel>
      ) : (
        renderTextBox()
      )}
      <CPErrorMessage
        fontSize="13px"
        error={dirty ? undefined : props.error}
        propertyName={props.name ?? "email"}
        errorMsg={
          !props.value
            ? undefined
            : isValid === false
              ? defaultErrorMsg
              : props.errorMsg
        }
      />
    </StackLayout>
  );
};

export default CPEmailTextBox;
